<template>
  <div>
    <b-card no-body class="mb-0">
      <div class="m-2">
        <!-- 標題和操作區域 -->
        <div class="d-flex flex-wrap flex-lg-nowrap align-items-center justify-content-between">
          <div class="d-flex align-items-center">
            <h3 class="font-weight-bolder text-nowrap mb-0">
              <feather-icon size="18" icon="FileTextIcon" />
              法院案件記錄
            </h3>
            <div class="form-col-select ml-2">
              <v-select
                v-model="perPage"
                :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                :options="perPageOptions"
                :clearable="false"
                class="per-page-selector d-inline-block"
              />
              <feather-icon size="18" icon="ChevronDownIcon" />
            </div>
          </div>

          <div class="d-flex align-items-center">
            <!-- 搜尋框 -->
            <div class="search-primary mr-2">
              <feather-icon size="16" icon="SearchIcon" />
              <b-form-input
                v-model="searchQuery"
                class="d-inline-block"
                placeholder="搜尋案件編號、當事人..."
              />
            </div>

            <!-- 日期篩選 -->
            <b-form-input
              v-model="selectedDate"
              type="date"
              class="date-selector mr-2"
              style="width: 150px;"
            />

            <!-- 法庭篩選 -->
            <v-select
              v-model="selectedCourt"
              :options="courtOptions"
              :clearable="true"
              placeholder="選擇法庭"
              class="court-selector mr-2"
            />

            <!-- 重新整理按鈕 -->
            <b-button
              variant="outline-primary"
              @click="refreshData"
              :disabled="loading"
            >
              <feather-icon size="16" icon="RefreshCwIcon" class="mr-1" />
              重新整理
            </b-button>
          </div>
        </div>
      </div>


      <!-- 資料表格 -->
      <b-table
        ref="courtCasesTable"
        :items="fetchRecords"
        :fields="tableColumns"
        :busy="loading"
        responsive
        show-empty
        empty-text="未找到匹配的案件"
        table-class="court-cases-table"
      >
        <!-- 載入狀態 -->
        <template #table-busy>
          <div class="text-center text-primary my-2">
            <b-spinner class="align-middle mr-2" />
            <strong>載入中...</strong>
          </div>
        </template>

        <!-- 案件日期欄位 -->
        <template #cell(case_date)="data">
          <b-badge variant="primary">{{ formatDate(data.item.case_date) }}</b-badge>
        </template>

        <!-- 案件編號欄位 -->
        <template #cell(case_number)="data">
          <div class="d-flex align-items-center">
            <strong class="text-primary">{{ data.item.case_number }}</strong>
          </div>
        </template>

        <!-- 案件類型欄位 -->
        <template #cell(case_type)="data">
          <b-badge
            :variant="getCaseTypeVariant(data.item.case_type)"
            class="text-capitalize"
          >
            {{ data.item.case_type }}
          </b-badge>
        </template>

        <!-- 法庭欄位 -->
        <template #cell(court)="data">
          <div class="text-wrap">
            <small class="text-muted">{{ data.item.court_code }}</small><br>
            {{ data.item.court }}
          </div>
        </template>

        <!-- 法官欄位 -->
        <template #cell(judge)="data">
          <span class="font-weight-semibold">{{ data.item.judge || '未指定' }}</span>
        </template>

        <!-- 控罪欄位 -->
        <template #cell(charge)="data">
          <b-badge variant="secondary">{{ data.item.charge || '未指定' }}</b-badge>
        </template>

        <!-- 訴訟各方欄位 -->
        <template #cell(parties)="data">
          <div class="text-wrap" style="max-width: 200px;">
            <span :title="data.item.parties">
              {{ truncateText(data.item.parties, 50) }}
            </span>
          </div>
        </template>

        <!-- 應訊代表欄位 -->
        <template #cell(representatives)="data">
          <div class="text-wrap" style="max-width: 200px;">
            <span :title="data.item.representatives">
              {{ truncateText(data.item.representatives, 50) }}
            </span>
          </div>
        </template>

        <!-- 操作欄位 -->
        <template #cell(actions)="data">
          <div class="text-center">
            <b-dropdown
              variant="link"
              no-caret
              :right="$store.state.appConfig.isRTL"
            >
              <template #button-content>
                <feather-icon
                  icon="MoreVerticalIcon"
                  size="16"
                  class="align-middle text-body"
                />
              </template>
              <b-dropdown-item @click="viewCase(data.item)">
                <feather-icon icon="EyeIcon" />
                <span class="align-middle ml-50">檢視詳情</span>
              </b-dropdown-item>
            </b-dropdown>
          </div>
        </template>
      </b-table>

      <!-- 分頁 -->
      <div class="mx-2 mb-2">
        <b-row>
          <b-col
            cols="12"
            sm="6"
            class="d-flex align-items-center justify-content-center justify-content-sm-start"
          >
            <span class="text-muted">顯示 {{ (currentPage - 1) * (perPage && perPage.value || 15) + 1 }} 到 {{ Math.min(currentPage * (perPage && perPage.value || 15), totalRecords) }} 筆，共 {{ totalRecords }} 筆記錄</span>
          </b-col>
          <b-col
            cols="12"
            sm="6"
            class="d-flex align-items-center justify-content-center justify-content-sm-end"
          >
            <b-pagination
              v-model="currentPage"
              :total-rows="totalRecords"
              :per-page="perPage && perPage.value || 15"
              first-number
              last-number
              class="mb-0 mt-1 mt-sm-0"
              prev-class="prev-item"
              next-class="next-item"
            >
              <template #prev-text>
                <feather-icon icon="ChevronLeftIcon" size="18" />
              </template>
              <template #next-text>
                <feather-icon icon="ChevronRightIcon" size="18" />
              </template>
            </b-pagination>
          </b-col>
        </b-row>
      </div>
    </b-card>

    <!-- 案件詳情 Modal -->
    <b-modal
      id="case-detail-modal"
      v-model="showDetailModal"
      title="案件詳情"
      size="lg"
      hide-footer
      scrollable
    >
      <div v-if="selectedCase">
        <b-row>
          <b-col cols="12">
            <h5 class="mb-3">基本資訊</h5>
          </b-col>
          <b-col lg="6" cols="12">
            <div class="mb-2">
              <strong>案件編號：</strong>
              <span class="text-primary">{{ selectedCase.case_number }}</span>
            </div>
            <div class="mb-2">
              <strong>案件日期：</strong>
              {{ formatDate(selectedCase.case_date) }}
            </div>
            <div class="mb-2">
              <strong>案件類型：</strong>
              <b-badge :variant="getCaseTypeVariant(selectedCase.case_type)">
                {{ selectedCase.case_type }}
              </b-badge>
            </div>
          </b-col>
          <b-col lg="6" cols="12">
            <div class="mb-2">
              <strong>法庭：</strong>
              {{ selectedCase.court }}
            </div>
            <div class="mb-2">
              <strong>法官：</strong>
              {{ selectedCase.judge || '未指定' }}
            </div>
            <div class="mb-2">
              <strong>控罪：</strong>
              {{ selectedCase.charge || '未指定' }}
            </div>
          </b-col>
          <b-col cols="12" class="mt-3">
            <h5 class="mb-3">詳細資訊</h5>
            <div class="mb-3">
              <strong>訴訟各方：</strong>
              <p class="mt-1">{{ selectedCase.parties || '未指定' }}</p>
            </div>
            <div class="mb-3">
              <strong>應訊代表：</strong>
              <p class="mt-1">{{ selectedCase.representatives || '未指定' }}</p>
            </div>
          </b-col>
        </b-row>
      </div>
    </b-modal>
  </div>
</template>

<script>
import {
  BCard,
  BRow,
  BCol,
  BFormInput,
  BButton,
  BTable,
  BBadge,
  BDropdown,
  BDropdownItem,
  BPagination,
  BSpinner,
  BModal
} from 'bootstrap-vue'
import vSelect from 'vue-select'
import axios from 'axios'
import { ref, computed, onMounted, watch } from '@vue/composition-api'

export default {
  components: {
    BCard,
    BRow,
    BCol,
    BFormInput,
    BButton,
    BTable,
    BBadge,
    BDropdown,
    BDropdownItem,
    BPagination,
    BSpinner,
    BModal,
    vSelect
  },
  setup(props, { root }) {
    // 響應式數據
    const loading = ref(false)
    const courtCasesTable = ref(null)
    const records = ref([])
    const totalRecords = ref(0)
    const currentPage = ref(1)
    const perPage = ref({ label: '15', value: 15 })
    const searchQuery = ref('')
    const selectedDate = ref('')
    const selectedCourt = ref(null)
    const selectedCase = ref(null)
    const showDetailModal = ref(false)


    // 配置選項
    const perPageOptions = [
      { label: '10', value: 10 },
      { label: '15', value: 15 },
      { label: '25', value: 25 },
      { label: '50', value: 50 },
      { label: '100', value: 100 }
    ]
    const courtOptions = [
      { label: '商業法庭 (BP)', value: 'BP' }
    ]


    // 表格欄位配置
    const tableColumns = [
      {
        key: 'case_date',
        label: '案件日期',
        sortable: true,
        thStyle: { width: '120px', verticalAlign: 'middle' },
        tdClass: 'align-middle'
      },
      {
        key: 'case_number',
        label: '案件編號',
        sortable: true,
        thStyle: { width: '140px', verticalAlign: 'middle' },
        tdClass: 'align-middle'
      },
      {
        key: 'case_type',
        label: '案件類型',
        sortable: true,
        thStyle: { width: '100px', verticalAlign: 'middle' },
        tdClass: 'align-middle'
      },
      {
        key: 'court',
        label: '法庭',
        sortable: true,
        thStyle: { width: '150px', verticalAlign: 'middle' },
        tdClass: 'align-middle'
      },
      {
        key: 'judge',
        label: '法官',
        sortable: true,
        thStyle: { width: '100px', verticalAlign: 'middle' },
        tdClass: 'align-middle'
      },
      {
        key: 'charge',
        label: '控罪',
        sortable: false,
        thStyle: { width: '100px', verticalAlign: 'middle' },
        tdClass: 'align-middle'
      },
      {
        key: 'parties',
        label: '訴訟各方',
        sortable: false,
        thStyle: { minWidth: '200px', verticalAlign: 'middle' },
        tdClass: 'align-middle'
      },
      {
        key: 'representatives',
        label: '應訊代表',
        sortable: false,
        thStyle: { minWidth: '200px', verticalAlign: 'middle' },
        tdClass: 'align-middle'
      },
      {
        key: 'actions',
        label: '操作',
        sortable: false,
        thClass: 'text-center',
        thStyle: { width: '80px', verticalAlign: 'middle' },
        tdClass: 'align-middle'
      }
    ]

    // 方法
    const fetchRecords = async (ctx) => {
      loading.value = true
      try {
        const token = localStorage.getItem('accessToken')
        const params = {
          page: ctx && ctx.currentPage || currentPage.value,
          per_page: perPage.value && perPage.value.value || 15,
          q: searchQuery.value || null,
          date: selectedDate.value || null,
          court_code: selectedCourt.value && selectedCourt.value.value || null
        }

        const response = await axios.get(
          `${process.env.VUE_APP_SERVICE_URL}/court-cases`,
          {
            params,
            headers: {
              Authorization: `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          }
        )

        if (response.data.success) {
          const data = response.data.data
          totalRecords.value = data.total
          return data.data || []
        }
        return []
      } catch (error) {
        console.error('獲取案件記錄失敗:', error)
        root.$toast({
          component: 'ToastificationContent',
          props: {
            title: '錯誤',
            text: '獲取案件記錄失敗，請重試',
            variant: 'danger'
          }
        })
        return []
      } finally {
        loading.value = false
      }
    }


    const refreshData = () => {
      root.$nextTick(() => {
        if (courtCasesTable.value) {
          courtCasesTable.value.refresh()
        }
      })
    }

    const viewCase = (caseItem) => {
      selectedCase.value = caseItem
      showDetailModal.value = true
    }

    const formatDate = (date) => {
      if (!date) return ''
      return new Date(date).toLocaleDateString('zh-TW')
    }

    const getCaseTypeVariant = (type) => {
      const typeMap = {
        '破產呈請': 'warning',
        '商業案件': 'primary',
        '民事案件': 'info'
      }
      return typeMap[type] || 'secondary'
    }

    const truncateText = (text, limit) => {
      if (!text) return '未指定'
      return text.length > limit ? text.substring(0, limit) + '...' : text
    }

    // 監聽器
    // 搜尋和篩選監聽器（防抖動）
    let searchTimeout = null
    watch([searchQuery, selectedDate, selectedCourt], () => {
      if (searchTimeout) clearTimeout(searchTimeout)
      searchTimeout = setTimeout(() => {
        currentPage.value = 1
        root.$nextTick(() => {
          if (courtCasesTable.value) {
            courtCasesTable.value.refresh()
          }
        })
      }, 500)
    })

    // perPage 變化立即觸發（不需要防抖動）
    watch(perPage, () => {
      currentPage.value = 1
      root.$nextTick(() => {
        if (courtCasesTable.value) {
          courtCasesTable.value.refresh()
        }
      })
    })

    // 生命週期
    // onMounted(() => {
    // })

    return {
      // 響應式數據
      loading,
      courtCasesTable,
      records,
      totalRecords,
      currentPage,
      perPage,
      searchQuery,
      selectedDate,
      selectedCourt,
      selectedCase,
      showDetailModal,

      // 配置
      perPageOptions,
      courtOptions,
      tableColumns,

      // 方法
      fetchRecords,
      refreshData,
      viewCase,
      formatDate,
      getCaseTypeVariant,
      truncateText
    }
  }
}
</script>

<style lang="scss">
@import '@core/scss/vue/libs/vue-select.scss';

.search-primary {
  position: relative;

  .feather-icon {
    position: absolute;
    left: 0.75rem;
    top: 50%;
    transform: translateY(-50%);
    z-index: 4;
  }

  .form-control {
    padding-left: 2.2rem;
    width: 200px;
  }
}

.form-col-select {
  position: relative;

  .feather-icon {
    position: absolute;
    right: 0.75rem;
    top: 50%;
    transform: translateY(-50%);
    z-index: 4;
    pointer-events: none;
  }
}

.per-page-selector {
  width: 90px;
}

.date-selector, .court-selector {
  width: 150px;
}

.court-cases-table {
  th, td {
    border-color: #ebe9f1;
  }

  th {
    background-color: #f8f8f8;
    font-weight: 600;
    font-size: 0.857rem;
  }
}

.text-wrap {
  word-wrap: break-word;
  white-space: normal;
}
</style>